import { MainFooter } from '@/components/Footer';
import { Header } from '@/components/Header';
import { LoggedUserAvatar } from '@/components/shared';
import { ROUTE_PATHS } from '@/constants';
import { pathJoin } from '@/utils';
import type { SystemProps } from '@chakra-ui/react';
import { Box, Container } from '@chakra-ui/react';
import { useMatch } from 'react-router-dom';

interface IUseWizardLayoutPropsReturnValue {
  containerProps: SystemProps;
  isProgressBarVisible: boolean;
  mainProps: SystemProps;
}

const useWizardLayoutProps = (): IUseWizardLayoutPropsReturnValue => {
  const matchDetailsPath = useMatch(pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quoteDetails]));

  const matchQuotePaymentSuccess = useMatch(
    pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quotePaymentSuccess])
  );

  const matchQuotePaymentError = useMatch(
    pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quotePaymentError])
  );

  const matchRequestedPath = useMatch(
    pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quoteRequestedConfirmation])
  );

  const matchCheckEmailPath = useMatch(
    pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.checkEmailMessage])
  );

  const isProgressBarVisible =
    !matchDetailsPath &&
    !matchQuotePaymentSuccess &&
    !matchCheckEmailPath &&
    !matchQuotePaymentError;

  const hasWizardStyles =
    !matchRequestedPath && !matchQuotePaymentSuccess && !matchQuotePaymentError;

  const mainProps = hasWizardStyles
    ? {
        bg: 'brand.sand',
      }
    : {
        bg: 'white',
        px: { base: 4, md: 8, xl: 36 },
        borderBottom: '1px',
        borderColor: 'brand.gray',
      };

  const containerProps = hasWizardStyles
    ? {
        bg: 'white',
        maxW: '8xl',
        px: { base: 4, sm: 8, md: 16, lg: 20 },
      }
    : {
        maxW: 'full',
      };

  return {
    isProgressBarVisible,
    containerProps,
    mainProps,
  };
};

interface IWizardLayoutProps {
  children: React.ReactNode;
  isLogged?: boolean;
}

export const WizardLayout: React.FC<IWizardLayoutProps> = ({
  children,
  isLogged = false,
}): JSX.Element => {
  const { containerProps, isProgressBarVisible, mainProps } = useWizardLayoutProps();
  return (
    <>
      <Header isProgressBarVisible={isProgressBarVisible}>
        {isLogged && isProgressBarVisible ? <LoggedUserAvatar /> : undefined}
      </Header>
      <Box as="main" py={16} w="full" {...mainProps}>
        <Container py={{ base: 8, lg: 20 }} {...containerProps}>
          {children}
        </Container>
      </Box>
      <Box as="footer" bg="white" py={12}>
        <Container maxW="8xl">
          <MainFooter />
        </Container>
      </Box>
    </>
  );
};
